import { FC } from 'react';
import { Box, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';
import { DomainDocumentComparisonChangeType } from '@libs/types';
import { CheckIcon, PlusIcon } from '@phosphor-icons/react';

interface AssessmentDocumentChangeBadgeProps {
  changeType: DomainDocumentComparisonChangeType | undefined;
  parentVersion: number | undefined;
}

/**
 * Renders a small chip next to a file's meta row indicating whether the
 * document was carried over from a previous assessment version
 * (`Unchanged` → "FROM V{N}") or newly added in the current draft
 * (`Added` → "NEW"). Returns null when there's no parent version or no
 * changeType — the badge only appears in re-assessment context.
 */
export const AssessmentDocumentChangeBadge: FC<AssessmentDocumentChangeBadgeProps> = observer(({
  changeType,
  parentVersion,
}) => {
  const theme = useTheme();

  if (changeType === DomainDocumentComparisonChangeType.Unchanged && parentVersion != null) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.125,
          borderRadius: 999,
          backgroundColor: 'common.white',
          border: `1px solid ${theme.palette.green[200]}`,
          color: 'green.700',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        <CheckIcon size={10} weight="bold" />
        <Trans
          i18nKey="app.assessment.workflow.documentBadge.fromVersion"
          values={{ parentVersion }}
        />
      </Box>
    );
  }

  if (changeType === DomainDocumentComparisonChangeType.Added) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.125,
          borderRadius: 999,
          backgroundColor: 'common.white',
          border: `1px solid ${theme.palette.blue[200]}`,
          color: 'blue.700',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        <PlusIcon size={10} weight="bold" />
        <Trans i18nKey="app.assessment.workflow.documentBadge.newInThisVersion" />
      </Box>
    );
  }

  return null;
});
